import type { MemoExoticComponent } from 'react';
import { memo, useEffect, useLayoutEffect, useRef } from 'react';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { InlineCommentSelectionComponentProps } from '@atlaskit/editor-common/types/annotation';

export type BaseSelectionComponentProps = {
	pageId: string;
	isArchived?: boolean;
	updateDocument?: (doc: JSONDocNode) => void;
	onCommentButtonMount?: () => void;
};

type SelectionManagerProps = BaseSelectionComponentProps & {
	setSelectionProps: (
		selectionProps: (InlineCommentSelectionComponentProps & BaseSelectionComponentProps) | null,
	) => void;
};

export const SelectionManager = ({
	pageId,
	isArchived,
	setSelectionProps,
	updateDocument,
	onCommentButtonMount,
}: SelectionManagerProps): MemoExoticComponent<
	(props: InlineCommentSelectionComponentProps) => null
> =>
	memo((props: InlineCommentSelectionComponentProps) => {
		const {
			range,
			draftRange,
			wrapperDOM,
			isAnnotationAllowed,
			onCreate,
			onClose,
			applyDraftMode,
			removeDraftMode,
			inlineNodeTypes,
		} = props;

		// Keep refs to applyDraftMode/removeDraftMode so that setSelectionOptionsForCreateEditor
		// always calls the latest Mounter instance's callbacks, even if Mounter unmounts and
		// remounts (e.g. after promoteHoverToDraft clears the type) without range/draftRange
		// changing (COMMENTS-6594).
		const applyDraftModeRef = useRef(applyDraftMode);
		const removeDraftModeRef = useRef(removeDraftMode);
		useLayoutEffect(() => {
			applyDraftModeRef.current = applyDraftMode;
			removeDraftModeRef.current = removeDraftMode;
		}, [applyDraftMode, removeDraftMode]);

		useEffect(() => {
			setSelectionProps({
				pageId,
				isArchived,
				updateDocument,
				range,
				draftRange,
				wrapperDOM,
				isAnnotationAllowed,
				onCreate,
				onClose,
				applyDraftMode: (...args) => applyDraftModeRef.current?.(...args),
				removeDraftMode: () => removeDraftModeRef.current?.(),
				inlineNodeTypes,
				onCommentButtonMount,
			});
			// We only want to listen to changes that signifies a new selection
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [range, draftRange, wrapperDOM]);

		return null;
	});
